import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { CMaxOrderItems } from '@core/availability-calculation/availability-calculation.const';
import { CheckoutHelperService } from '@core/checkout/checkout-helper.service';
import { IOrderItemSelection } from '@core/checkout/model/checkout-order-item-selection.model';
import { DateFormatService } from '@core/date/date-format.service';
import { DateRangeFormatService } from '@core/date/date-range-format.service';
import { INumberIncrementProductItem } from '@core/form/number-increment/number-increment-product-item.model';
import { MinBookingLimitValidator } from '@validators/min-booking-limit/min-booking-limit.validator';
import { IProductInfo } from '@core/product/models/product-info.model';
import { IProductPackageItem } from '@core/product/models/product-package-item.model';
import { ISessionDiscount } from '@core/product/models/product-session-discount.model';
import { IProductPackageSession, IProductSession } from '@core/product/models/product-session.model';
import { TProductType } from '@core/product/models/product-type.type';
import { IProduct } from '@core/product/models/product.model';
import { ProductHelperService } from '@core/product/product-helper.service';
import { WhenAttendingSessionService } from '@app/when-attending/when-attending-session/when-attending-session.service';
import { hexGenerate } from '@roller/rdl/utilities/string';
import { ProductAvailableService } from '@core/product/product-available.service';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IProductAllocation } from '@core/product/models/product-allocation.model';
import { IProductPackageOptionSet } from '@app/core/product/models/product-option-set.model';
import { IOptionSetSelection } from '@app/core/checkout/model/checkout-option-set-selection.model';

@Injectable({
  providedIn: 'root'
})
export class ProductOrderingService {
  private _relatedBasketKey = hexGenerate(15);

  constructor(
    private dateFormatService: DateFormatService,
    private dateRangeFormatService: DateRangeFormatService,
    private productHelperService: ProductHelperService,
    private whenAttendingSessionService: WhenAttendingSessionService,
    private checkoutHelperService: CheckoutHelperService,
    private minBookingLimit: MinBookingLimitValidator,
    private productAvailableService: ProductAvailableService
  ) {}

  /**
   * Creates the sellable items form for the product selected. Should be run after availability is fetched and addonList availability filtered
   * @param product parent product
   * @param packageItems list of available package items
   * @returns Product dialog form group
   */
  createForm(
    parentProduct: IProduct,
    filteredTickets: IProductInfo[],
    ticketsUserDefined: IProductInfo[][],
    form?: UntypedFormGroup
  ): UntypedFormGroup {
    const returnGroup: UntypedFormGroup = form || new UntypedFormGroup({});

    // add the products to the form.
    filteredTickets.forEach((info: IProductInfo) => {
      // set up number increment validators
      const validateItem: ValidatorFn[] = this.checkoutHelperService.addMinMaxValidators(info, info.currentItemMax);
      returnGroup.addControl(`product-info-${info.id}`, new FormControl<number>(0, validateItem));
    });

    // create package item form array based on filtered this.packageItems
    if (parentProduct.type === 'partypackage' && !!filteredTickets?.length) {
      filteredTickets.forEach((info: IProductInfo) => {
        const formGroups: UntypedFormGroup[] = !info.packageItems
          ? []
          : info.packageItems.map(() => new UntypedFormGroup({}));
        const formPackageArray = new UntypedFormArray(formGroups);
        returnGroup.addControl(`package-items-${info.id}`, formPackageArray);
      });
    }

    // create option set form array based on filtered this.productOptionSets
    if (parentProduct.type === 'package' && !!filteredTickets?.length) {
      filteredTickets.forEach((info: IProductInfo) => {
        const formGroups: UntypedFormGroup[] = !info.packageOptionSets
          ? []
          : info.packageOptionSets.map(() => new UntypedFormGroup({}));
        const formProductOptionSetArray = new UntypedFormArray(formGroups);
        returnGroup.addControl(`product-option-set-${info.id}`, formProductOptionSetArray);
      });
    }

    // create separate array of form items for tickets with user defined costs
    if (ticketsUserDefined?.length) {
      const userDefinedFormArray = this.addUserDefinedControlsToForm(ticketsUserDefined.flat());
      returnGroup.addControl('userDefinedCost', userDefinedFormArray);
    }

    return returnGroup;
  }

  /**
   * Add package items that require a user selection to the form.
   * Any filtering should be done prior to this. Note that this mutates
   * @returns the FormGroup with the additional formControls
   */
  addPackageItemToForm(
    packageItemProduct: IProductPackageItem,
    formPackageItem: UntypedFormGroup,
    index: number,
    orderSelections: IOrderItemSelection[] | undefined
  ): UntypedFormGroup {
    if (packageItemProduct.isSingleSelection) {
      const validators: ValidatorFn[] = [Validators.required];
      const itemSelection = orderSelections?.find(
        (orderSelection: IOrderItemSelection) => orderSelection.parentProductId === packageItemProduct.parentProductId
      );
      formPackageItem?.addControl(
        `party-package-item-${index}-${packageItemProduct.productId}`,
        new UntypedFormControl(itemSelection?.productId, validators)
      );
    } else {
      packageItemProduct.variations.forEach((variationProduct: IProductInfo) => {
        const validators: ValidatorFn[] = [Validators.min(0)];
        const itemSelection = orderSelections?.find(
          (orderSelection: IOrderItemSelection) => orderSelection.productId === variationProduct.id
        );
        formPackageItem?.addControl(
          `party-package-item-${index}-variation-${variationProduct.id}`,
          new FormControl<number>(itemSelection?.quantity || 0, validators)
        );
      });
    }

    return formPackageItem;
  }

  /**
   * Add package options that require a user selection to the form.
   * Any filtering should be done prior to this. Note that this mutates
   * @returns the FormGroup with the additional formControls
   */
  addOptionSetSelectionToForm(
    productOptionSet: IProductPackageOptionSet,
    formProductOptionSet: UntypedFormGroup,
    ticketId: string,
    index: number,
    optionSetSelections: IOptionSetSelection[] | undefined
  ): UntypedFormGroup {
    productOptionSet.variations.forEach((variationProduct: IProductInfo) => {
      const validators: ValidatorFn[] = [Validators.min(0)];
      const itemSelection = optionSetSelections?.find(
        (optionSetSelection: IOptionSetSelection) => optionSetSelection.productId === variationProduct.id
      );
      formProductOptionSet?.addControl(
        `product-option-set-${ticketId}-${index}-variation-${variationProduct.id}`,
        new FormControl<number>(itemSelection?.quantity || 0, validators)
      );
    });

    return formProductOptionSet;
  }

  /**
   * Creates a form array for all custom value tickets, and a separate array for each product id
   * Adds the first form group for each product Id to the array
   * @param products list of custom value products
   * @returns FormArray
   */
  addUserDefinedControlsToForm(products: IProductInfo[]): UntypedFormArray {
    const uniqueProductsFormArray: UntypedFormArray = new UntypedFormArray([]);

    products.forEach((info: IProductInfo) => {
      const userDefinedForms: UntypedFormArray = new UntypedFormArray([]);

      userDefinedForms.push(this.getUserDefinedFormGroup(info));

      uniqueProductsFormArray.push(userDefinedForms);
    });

    return uniqueProductsFormArray;
  }

  /**
   * Creates a custom value product form group
   * @param productInfo product to create the FormGroup
   * @param index FormGroup parent array index
   * @returns custom value FormGroup
   */
  getUserDefinedFormGroup(productInfo: IProductInfo, index = 0): UntypedFormGroup {
    const valueInputValidators: ValidatorFn[] = [
      Validators.min(productInfo.minUserDefinedCost || 0),
      Validators.max(productInfo.maxUserDefinedCost || 1)
    ];

    const productControlGroup = new UntypedFormGroup({});

    const inputControlName = `user-defined-input-${productInfo.id}-${index}`;
    productControlGroup.addControl(inputControlName, new UntypedFormControl(null, valueInputValidators));

    const quantityControlName = `user-defined-quantity-${productInfo.id}-${index}`;
    productControlGroup.addControl(quantityControlName, new FormControl<number>(0, [Validators.min(0)]));

    return productControlGroup;
  }

  /** If the control has exceeded the currentItemMax then reset the form to the currentItem max to prevent unusual errors. */
  resetFormControlToMaxValue(
    ev: INumberIncrementProductItem,
    form: UntypedFormGroup,
    productType: TProductType | undefined
  ): void {
    ev.quantity = ev.product.currentItemMax || CMaxOrderItems;
    let productControl: FormControl | undefined;
    if (ev.product.type !== 'addon' || productType === 'addon') {
      const controlName = `product-info-${ev.product.id}`;
      productControl = form?.get(controlName) as FormControl;
    } else {
      const controlName = `addon-product-${ev.product.id}`;
      const formAddon: UntypedFormArray | undefined = form?.get('addons') as UntypedFormArray;
      formAddon?.controls.some((addonGroups: AbstractControl) => {
        const addonGroup = addonGroups as UntypedFormGroup;

        if (Object.keys(addonGroup.getRawValue()).includes(controlName)) {
          productControl = addonGroup.get(controlName) as FormControl;
          return true;
        }

        return false;
      });
    }

    if (productControl) {
      productControl.setValue(ev.product.currentItemMax, { emitEvent: true });
    }
  }

  /** Iterates through the formControls to reset all values to 0. */
  resetForm(tickets: IProductInfo[], form: UntypedFormGroup | undefined): void {
    if (!form) {
      return;
    }

    tickets.forEach((info: IProductInfo) => {
      this.updateFormItem(form, info.id, 0);
    });
  }

  /** Updates a specified product-info-{id} form control with {quantity} */
  updateFormItem(form: UntypedFormGroup | undefined, id: string, quantity: number, controlPrefix?: string): void {
    if (!form) {
      return;
    }

    controlPrefix = controlPrefix || 'product-info-';
    const control = form.get(`${controlPrefix}${id}`);
    if (!form || !control) {
      return;
    }

    control.setValue(quantity);
  }

  /** Gets the remaining recurring sessions for any recurring session tickets given */
  getRemainingRecurringSessions(tickets: IProductInfo[]): string {
    const remainingSessions = tickets
      .map((p: IProductInfo) => p.remainingRecurringSessions)
      .filter((count: number | undefined): count is number => !!count)
      .sort((a: number, b: number) => a - b);

    if (remainingSessions.length > 0) {
      const minRemainingSessions = remainingSessions.at(0);
      const maxRemainingSessions = remainingSessions.at(-1);
      if (minRemainingSessions === maxRemainingSessions) {
        return `${minRemainingSessions}`;
      } else {
        return `${minRemainingSessions}-${maxRemainingSessions}`;
      }
    } else {
      return '';
    }
  }

  /** Gets the days of the week for any recurring session tickets given  */
  getRecurringSessionDays(tickets: IProductInfo[]): string {
    const recurringSessionsDates = tickets
      .flatMap((ticket: IProductInfo) => ticket.recurringSessionDates ?? [])
      .map((date: string) => this.dateFormatService.dateOnlyStringToDate(date));

    return this.dateRangeFormatService.daysOfWeekRangeString(recurringSessionsDates);
  }

  /** Gets the date range spanned by any recurring session tickets given  */
  getRecurringSessionDates(tickets: IProductInfo[]): string {
    const recurringSessionsDates = tickets
      .flatMap((ticket: IProductInfo) => ticket.recurringSessionDates ?? [])
      .map((date: string) => this.dateFormatService.dateOnlyStringToDate(date));

    return this.dateRangeFormatService.shortDateRangeString(recurringSessionsDates);
  }

  /** Get private member for current related basket Key */
  getRelatedBasketKey(): string {
    return this._relatedBasketKey;
  }

  /** Set the private member for related basket Key */
  setRelatedBasketKey(key: string | undefined): void {
    this._relatedBasketKey = key || this._relatedBasketKey;
  }

  /** For tickets with valid consecutive sessions, adds computed discount data and a 'default' session item, to create a menu in the UI */
  hydrateSessionDiscounts(
    tickets: IProductInfo[],
    session: IProductSession,
    allSessions: IProductSession[] | undefined,
    orderItems: IOrderItem[],
    bookingDate: Date,
    displayCapacityAsResources?: boolean
  ): void {
    const currentSessionIndex =
      allSessions?.findIndex((item: IProductSession) =>
        this.whenAttendingSessionService.isSameSessionGroup(session, item)
      ) ?? -1;
    if (!allSessions || currentSessionIndex === -1) {
      return;
    }

    const sessionsRemaining = allSessions.slice(currentSessionIndex + 1);

    tickets.forEach((ticket: IProductInfo) => {
      if (!ticket.sessionDiscounts?.length) {
        return;
      }

      let discounts: ISessionDiscount[] = this.getSessionDiscountCostsAndDurations(
        ticket,
        session,
        sessionsRemaining,
        orderItems,
        bookingDate,
        displayCapacityAsResources
      );

      if (!ticket.sessionDiscounts.find((discount: ISessionDiscount) => discount.numberOfSessions === 1)) {
        const defaultDuration: ISessionDiscount = {
          amountOff: 0,
          percentOff: 0,
          numberOfSessions: 1,
          duration: this.productHelperService.calculateSessionLength(session.startTime, session.endTime),
          computedCost: ticket.cost,
          capacityRemaining: displayCapacityAsResources
            ? session.resourceCapacityRemaining
            : session.ticketCapacityRemaining,
          endTime: session.endTime
        };
        discounts = [defaultDuration].concat(discounts);
      }

      if (discounts.filter((discount: ISessionDiscount) => !!discount.duration).length <= 1) {
        // if there's only one visible discount (i.e. the default), clear applied discount to hide the menu
        ticket.appliedSessionDiscount = undefined;
      } else {
        const activeDiscount = discounts.find(
          (discount: ISessionDiscount) => discount.numberOfSessions === ticket.appliedSessionDiscount?.numberOfSessions
        );
        ticket.appliedSessionDiscount = activeDiscount || discounts[0];
      }

      ticket.sessionDiscounts = discounts;
    });
  }

  /** Adds a computed cost and display string to the input discounts, if they form consecutive sessions with the input session */
  private getSessionDiscountCostsAndDurations(
    productInfo: IProductInfo,
    currentSession: IProductSession,
    sessionsRemaining: IProductSession[],
    orderItems: IOrderItem[],
    bookingDate: Date,
    displayCapacityAsResources?: boolean
  ): ISessionDiscount[] {
    const discounts = productInfo.sessionDiscounts ?? [];
    const minGroupSize = productInfo.groupSize * (productInfo.forceMinPurchase ? productInfo.minPurchase : 1);

    /** Exclude the current session order item from the capacity calculation for consecutive sessions. */
    const orderItemsExcludingCurrentSession = orderItems.filter(
      (item) =>
        item.productId !== productInfo.id || !this.productHelperService.isSameSession(currentSession, item.session)
    );

    return discounts.map((discount: ISessionDiscount) => {
      // find a chain of consecutive sessions by matching start/end times
      const consecutiveSessions = sessionsRemaining.reduce(
        (consecutiveSessions: IProductSession[], nextSession: IProductSession) => {
          if (
            consecutiveSessions.length < discount.numberOfSessions &&
            nextSession.startTime === consecutiveSessions.at(-1)?.endTime &&
            nextSession.allocations.some((allocation: IProductAllocation) => allocation.productId === productInfo.id)
          ) {
            consecutiveSessions.push(nextSession);
          }

          return consecutiveSessions;
        },
        [currentSession]
      );

      const resourceCapacitiesRemaining = consecutiveSessions
        .map((session) => session.resourceCapacityRemaining)
        .filter((resourceCapacityRemaining) => resourceCapacityRemaining !== undefined) as number[];

      const resourceCapacityRemaining = resourceCapacitiesRemaining.length
        ? Math.min(...resourceCapacitiesRemaining)
        : null;
      const bookableCapacitiesRemaining = consecutiveSessions.map((session) =>
        this.productAvailableService.getRemainingCapacity(
          productInfo.id,
          orderItemsExcludingCurrentSession,
          bookingDate,
          session
        )
      );

      const bookableCapacityRemaining = bookableCapacitiesRemaining.length
        ? Math.min(...bookableCapacitiesRemaining)
        : CMaxOrderItems;

      let computedCost: number | undefined;
      let duration: string | null | undefined;
      let endTime: string | undefined;

      if (bookableCapacityRemaining >= minGroupSize && consecutiveSessions.length === discount.numberOfSessions) {
        computedCost = this.productHelperService.getConsecutiveSessionTicketCost(productInfo.cost, discount);
        const finalSession = consecutiveSessions.at(-1) as IProductSession;
        duration = this.productHelperService.calculateSessionLength(currentSession.startTime, finalSession.endTime);
        endTime = finalSession.endTime;
      }

      const capacityRemaining = displayCapacityAsResources ? resourceCapacityRemaining : bookableCapacityRemaining;

      return {
        ...discount,
        computedCost,
        duration,
        capacityRemaining,
        endTime
      };
    });
  }

  /**
   * Groups sessions for a package together for selection in the UI.
   * This allows sessions with the same date and name (start time) to be selected simultaneously,
   * displaying all package variations for those sessions. e.g. 10:00am
   */
  groupPackageSessions(packageSessions: IProductSession[] | undefined): IProductPackageSession[] | undefined {
    if (!packageSessions) {
      return;
    }

    return packageSessions
      .sort((a: IProductSession, b: IProductSession) => {
        //Assumption: we still want to sort chronologically regardless of custom session names.
        if (a.startTime === b.startTime) {
          return 0;
        }

        return a.startTime < b.startTime ? -1 : 1;
      })
      .reduce((groups: IProductPackageSession[], current: IProductSession) => {
        const previousGroup = groups.at(-1);
        const currentGroup: IProductPackageSession = {
          date: current.date,
          startTime: current.startTime,
          name: current.name,
          ticketCapacityRemaining: current.ticketCapacityRemaining,
          resourceCapacityRemaining: current.resourceCapacityRemaining,
          packageSessions: [current],
          onlineSalesOpen: current.onlineSalesOpen,
          allocations: current.allocations
        };
        if (!!previousGroup && this.whenAttendingSessionService.isSameSessionGroup(previousGroup, currentGroup)) {
          previousGroup.ticketCapacityRemaining = this.maxTicketCapacityRemaining(previousGroup, current);
          previousGroup.resourceCapacityRemaining = this.maxResourceCapacityRemaining(previousGroup, current);
          previousGroup.packageSessions = [...previousGroup.packageSessions, current];
          previousGroup.allocations = [...previousGroup.allocations.concat(currentGroup.allocations)];
          previousGroup.onlineSalesOpen = previousGroup.packageSessions.some(
            (productSession) => productSession.onlineSalesOpen
          );
        } else {
          groups.push(currentGroup);
        }

        return groups;
      }, []);
  }

  /** Returns the highest resource capacity remaining of a group of sessions and a session to be added to it */
  private maxResourceCapacityRemaining(
    packageSession: IProductPackageSession,
    session: IProductSession
  ): number | undefined {
    if (packageSession.resourceCapacityRemaining === undefined || session.resourceCapacityRemaining === undefined) {
      return packageSession.resourceCapacityRemaining ?? session.resourceCapacityRemaining;
    }

    return Math.max(packageSession.resourceCapacityRemaining, session.resourceCapacityRemaining);
  }

  /** Returns the highest tickets capacity remaining of a group of sessions and a session to be added to it */
  private maxTicketCapacityRemaining(
    packageSession: IProductPackageSession,
    session: IProductSession
  ): number | undefined {
    if (packageSession.ticketCapacityRemaining === undefined || session.ticketCapacityRemaining === undefined) {
      return packageSession.ticketCapacityRemaining ?? session.ticketCapacityRemaining;
    }

    return Math.max(packageSession.ticketCapacityRemaining, session.ticketCapacityRemaining);
  }

  /**
   * On basket updates, re calculate each increment form control based on the availability calc and product config
   * @param productControl form control to be adjusted
   * @param productInfo product details
   * @param currentValue current item value
   * @param maxAmountRemaining max amount remaining to be added
   */
  calculateControlValueValidity(
    productControl: AbstractControl | null | undefined,
    productInfo: IProductInfo,
    currentValue: number,
    maxAmountRemaining: number
  ): void {
    productInfo.isIncreaseDisabled = maxAmountRemaining <= 0;
    productInfo.currentItemMax = maxAmountRemaining + currentValue;

    if (productControl) {
      productControl.setValidators(
        this.checkoutHelperService.addMinMaxValidators(productInfo, productInfo.currentItemMax)
      );
      productControl.updateValueAndValidity();

      if (productInfo.isIncreaseDisabled && productInfo.currentItemMax === 0 && productControl.enabled) {
        productControl.disable();
      } else if (productControl.disabled && !productInfo.isIncreaseDisabled) {
        productControl.enable();
      }
    }
  }
}
